// --- React
import React, { useCallback, useMemo } from "react";
// --- UI Components
import Button from "@dtci/spellbook/Button";

/**
 * ClickableCellRenderer Component
 *
 * This component is a cell renderer for AG Grid that renders a clickable button.
 * It takes an action function and a button icon as props.
 * When the button is clicked, it performs the specified action.
 *
 * @param {Function} action - The action to be performed when the button is clicked.
 * @param {string} buttonIcon - The icon to be displayed on the button.
 * @param {Function} [shouldRender] - Optional validation function that receives data and returns boolean.
 *                                    If provided and returns false, the button is not rendered.
 * @param {Object} [customStyle] - Optional custom styles to apply to the button.
 * @param {Function} [getDisabled] - Optional function that receives data and returns boolean.
 *                                   If provided and returns true, the button is disabled.
 * @returns {JSX.Element|null} The rendered button component or null if shouldRender returns false.
 **/
export const ClickableCellRenderer = (
  action,
  buttonIcon,
  shouldRender,
  customStyle = {},
  getDisabled,
) => {
  const Renderer = ({ value, data, style = {} }) => {
    const isDisabled = getDisabled ? getDisabled(data) : false;
    // Use red color for destructive actions (trash icon)
    const isDestructive = buttonIcon?.includes("trash");
    const defaultColor = isDestructive ? "#e53935" : "#3611c8";

    const mergedStyle = useMemo(
      () => ({
        color: isDisabled ? "#999" : defaultColor,
        ...customStyle,
        ...style,
        ...(isDisabled ? { cursor: "not-allowed", opacity: 0.6 } : {}),
      }),
      [isDisabled, defaultColor, style],
    );

    const handleClick = useCallback(() => {
      if (!isDisabled) {
        action(data);
      }
    }, [isDisabled, data]);

    // If shouldRender is provided and returns false, don't render the button
    if (shouldRender && !shouldRender(data)) {
      return null;
    }

    return (
      <Button
        style={mergedStyle}
        iconType={`fa-regular ${buttonIcon}`}
        type="button"
        styleType="link"
        className="sb-typography sb-typography_T20"
        onClick={handleClick}
        disabled={isDisabled}
      >
        {value}
      </Button>
    );
  };

  return Renderer;
};
